// Domestic H5 develop@e4196f67 presentation adapter. N8 transport and units stay explicit.
import moment from 'moment'
import infraredDescriptions from './m60sInfraredDescriptions.json'
import uiEnglish from './m60sDomesticUiTexts.json'
import uiOverrides from './m60sDomesticUiOverrides.json'
import vitalTexts from './m60sVitalTexts.json'
import infraredTexts from './m60sInfraredTexts.json'
import { m60sVitalText } from './m60sVitalText'
import { frameParts, infraredText, temperature } from './m60sInfrared'
import { buildPartChangeText, buildPartDistributionText, buildPartAsymmetryText, buildOverallChangeFocusText, buildOverallDistributionText, buildOverallAsymmetryText } from './m60sInfraredRules'

const reuse = {
    '区域差值': ['infrared', 'infrared.metric.regionTempDiff'],
    '该部位不做左右差异评估。': ['infrared', 'infrared.asymmetry.unsupported'],
    '压力指数': ['vital', 'metric_si'],
    '放松': ['vital', 'status_relaxed'], '低压力': ['vital', 'status_low_stress'],
    '正常压力': ['vital', 'status_normal_stress'], '压力偏高': ['vital', 'status_elevated_stress'],
    '高压力': ['vital', 'status_high_stress'], '极高压力': ['vital', 'status_extreme_stress'],
    '心率偏低': ['vital', 'status_hr_low'], '心率偏高': ['vital', 'status_hr_high'],
    '呼吸偏低': ['vital', 'status_rr_low'], '呼吸偏高': ['vital', 'status_rr_high'],
    '明显偏低': ['vital', 'status_spo2_very_low'], '正常': ['vital', 'status_normal'], '偏低': ['vital', 'status_low'],
    '*为保证测量结果稳定性，需保持面部无遮挡、身体静止！': ['vital', 'condition_tip'],
    '声明：生命体征测量结果仅用于健康参考和趋势观察，不作为医学诊断依据。': ['vital', 'disclaimer'],
    '上次': ['infrared', 'infrared.ui.previous'], '本次': ['infrared', 'infrared.ui.current'],
    '暂无热图': ['infrared', 'infrared.heatmap.noImage'],
}
const sourcePairs = [['infrared', infraredTexts], ['vital', vitalTexts]]
export function domesticText(locale, chinese) {
    if (locale === 'zh-CN') return chinese
    const localizedUi = uiOverrides[locale] || {}
    if (localizedUi[chinese]) return localizedUi[chinese]
    let match = reuse[chinese]
    if (!match) {
        for (const [kind, texts] of sourcePairs) {
            const key = Object.keys(texts['zh-CN']).find(key => texts['zh-CN'][key] === chinese)
            if (key) { match = [kind, key]; break }
        }
    }
    if (match) {
        const text = match[0] === 'vital' ? m60sVitalText(locale, match[1]) : infraredText(locale, match[1])
        if (text !== match[1]) return text
    }
    return uiEnglish[chinese] || chinese
}
export function historyTime(value) {
    // N8 scanTime is a database wall-clock string. Do not reinterpret it as UTC.
    const date = typeof value === 'number' ? moment.unix(value) : moment(value, 'YYYY-MM-DD HH:mm:ss', true)
    return date.isValid() ? { label: date.format('YYYY/MM/DD HH:mm:ss'), date: date.format('YYYY/MM/DD'), time: date.format('HH:mm:ss'), createTime: date.unix() } : { label: '—', date: '—', time: '', createTime: 0 }
}
export function vitalView(source, previous, locale) {
    const t = key => m60sVitalText(locale, key)
    const metrics = source.assessment && Array.isArray(source.assessment.metrics) ? source.assessment.metrics : []
    return {
        measureTime: historyTime(source.scanTime).createTime,
        overall: {},
        metrics: metrics.map(metric => ({ ...metric,
            name: t('metric_' + metric.key), definition: t('definition_' + metric.key),
            unit: metric.key === 'hr' ? t('unit_hr') : ({ hrv: 'ms', si: '', rr: t('unit_rr'), spo2: t('unit_spo2') })[metric.key],
            statusName: !metric.statusKey || metric.statusKey === 'none' ? '' : t('status_' + metric.statusKey),
            labels: (metric.labelKeys || []).map(key => t('status_' + key)),
            deltaText: previous && previous.deltas ? previous.deltas[metric.key] || '--' : '--',
        })),
        isFirstMeasure: !previous, hasHistory: !!previous,
        disclaimer: domesticText(locale, '声明：生命体征测量结果仅用于健康参考和趋势观察，不作为医学诊断依据。'),
    }
}
export function infraredView(source, previous, locale, imperial = false) {
    const t = (key, params) => infraredText(locale, key, params)
    const parts = frameParts(source), previousParts = frameParts(previous || {})
    const previousMap = Object.fromEntries(previousParts.map(p => [p.code, p]))
    // Conclusion rules run on Celsius. Only absolute/difference display values are converted.
    const conclusion = text => imperial ? text.replace(/([↑↓]?)([+-]?\d+(?:\.\d+)?)\s*(?:℃|°C)/g, (_, arrow, value) => arrow + temperature(value, true, true)) : text
    const image = (frame, type) => { const file = (frame.files || []).find(f => Number(f.typeId) === type); return file && /^https?:\/\//i.test(file.filePath || '') ? file.filePath : '' }
    const valid = parts.filter(p => p.value !== null)
    const comparable = valid.filter(p => previousMap[p.code] && previousMap[p.code].value !== null)
    const rows = []
    if (previous && comparable.length) rows.push({ label: t('infrared.overall.changeFocus'), text: conclusion(buildOverallChangeFocusText(t, comparable, previousMap)) })
    if (valid.length >= 2) rows.push({ label: t('infrared.overall.distribution'), text: conclusion(buildOverallDistributionText(t, valid)) })
    const asymmetric = parts.filter(p => p.extra.supportsAsymmetry)
    if (asymmetric.length) rows.push({ label: t('infrared.overall.asymmetry'), text: conclusion(buildOverallAsymmetryText(t, asymmetric)) })
    rows.push({ label: t('infrared.overall.serviceTip'), text: t('infrared.overall.serviceTip.text') })
    return {
        measureTime: historyTime(source.scanTime).createTime,
        isFirstMeasure: !previous, overallAssessment: rows,
        frontImageUrl: image(source, 53), backImageUrl: image(source, 54),
        compareFrontImageUrl: image(previous || {}, 53), compareBackImageUrl: image(previous || {}, 54),
        roiPoints: mapRois(source.rois), compareRoiPoints: mapRois(previous && previous.rois),
        parts: parts.map(p => {
            const before = previousMap[p.code], comparable = p.value !== null && before && before.value !== null
            return { key: p.code, partCode: p.code, name: t(p.nameKey), present: p.value,
                desc: domesticText(locale, infraredDescriptions[p.code]),
                last: before ? before.value : null, change: comparable ? Math.round((p.value - before.value) * 10) / 10 : null,
                high: p.extra.regionMaxTemp, low: p.extra.regionMinTemp, regionTempDiff: p.extra.regionTempDiff,
                leftAvgTemp: p.code === 'spine' ? null : p.extra.leftAvgTemp,
                rightAvgTemp: p.code === 'spine' ? null : p.extra.rightAvgTemp,
                leftRightTempDiff: p.code === 'spine' ? null : p.extra.leftRightTempDiff,
                view: [p.view],
                changeText: comparable ? conclusion(buildPartChangeText(t, p, before)) : '',
                distributionText: p.extra.regionTempDiff === null ? '' : conclusion(buildPartDistributionText(t, p)),
                symmetryText: p.extra.supportsAsymmetry ? conclusion(buildPartAsymmetryText(t, p)) : p.code === 'spine' ? domesticText(locale, '该部位不做左右差异评估。') : '',
                serviceText: t('infrared.serviceTip.part'),
            }
        }),
    }
}
export function mapRois(rois) {
    return (rois || []).filter(r => r.coordinateSpace === 'image-pixels' && [r.x, r.y, r.width, r.height].every(Number.isFinite) && r.width > 0 && r.height > 0)
        .map(r => ({ key: r.code, view: r.view, points: [{ x: r.x, y: r.y }, { x: r.x + r.width, y: r.y + r.height }] }))
}
